import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { isEqual } from 'lodash';

import { getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import {
  PATH_EXTERNAL_PEOPLE,
  PATH_INTERNAL_PEOPLE,
  PATH_PERSON_GENERAL,
} from '../../../app/constants/paths.constant';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ThirdPartiesCmp_SomePeople_QueryDocument } from '../../../components/ThirdParties/queries/some-people.query';
import { PersonPage_OnePerson_QueryDocument } from '../../../pages/Person/hooks/useFindOnePersonQuery.hook';
import { UserPage_OneUser_QueryDocument } from '../../../pages/User';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';

import { personForm2ApiMapper } from '../mappers/person.form2api.mapper';
import { getPersonIdInResult } from '../utils/get-person-id-in-result.util';
import type { ThirdPartyFormTab } from '../enums/tab.enum';
import type { ThirdPartyForm_Person } from '../fragments/person.fragment';
import type { PersonMutationResult } from '../types/person-mutation-result.type';

import { useCreatePersonMutation } from './useCreatePersonMutation.hook';
import { useUpdatePersonMutation } from './useUpdatePersonMutation.hook';
import { useUpdateEmittersMutation } from './useUpdateEmittersMutation.hook';

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: PersonMutationResult) => void;
  readonly initialValues: ThirdPartyForm_Person;
  readonly personId?: Id;
}

export const usePersonSubmit = ({
  formVariant,
  handleSuccess,
  initialValues,
  personId,
}: Readonly<Params>) => {
  const { genericStatusesCodeMap } = useReferencesContext();

  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createPersonMutation } = useCreatePersonMutation();
  const { mutate: updatePersonMutation } = useUpdatePersonMutation();
  const { mutate: updateEmittersMutation } = useUpdateEmittersMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<ThirdPartyForm_Person, ThirdPartyFormTab>
    >) => {
      setWizardError(undefined);

      const mustUpdateEmitters = !isEqual(
        values.billEmitterIds,
        initialValues.billEmitterIds,
      );

      const conclude = (result: PersonMutationResult) => {
        if (!formVariant || formVariant === FormVariant.Creation) {
          enqueueSnackbar('La nouvelle personne a bien été créée.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_PERSON_GENERAL, {
                id: getPersonIdInResult(result),
              }),
            ),
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Enabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
        } else {
          enqueueSnackbar('La personne a bien été mise à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Enabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument),
              {
                statusId: genericStatusesCodeMap.Disabled._id,
                isInternal: values.isInternal,
              },
            ],
          });
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(PersonPage_OnePerson_QueryDocument),
              {
                id: personId,
              },
            ],
          });
          // MHO : We have no easy way to know if the person is linked to a user, so we invalidate all user queries just in case
          void queryClient.invalidateQueries({
            queryKey: [getDocumentName(UserPage_OneUser_QueryDocument)],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(
            values.isInternal ? PATH_INTERNAL_PEOPLE : PATH_EXTERNAL_PEOPLE,
          );
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      const onSuccess = (result: PersonMutationResult) => {
        if (!mustUpdateEmitters) {
          conclude(result);
          return;
        }

        updateEmittersMutation(
          {
            emitterIds: values.billEmitterIds,
            thirdPartyId: getPersonIdInResult(result),
          },
          {
            onSuccess: () => {
              conclude(result);
            },
          },
        );
      };

      if (!formVariant || formVariant === FormVariant.Creation) {
        createPersonMutation(
          {
            person: personForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else if (personId) {
        updatePersonMutation(
          {
            filter: { _id: personId },
            update: personForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      buildViewSnackbarAction,
      createPersonMutation,
      enqueueSnackbar,
      formVariant,
      genericStatusesCodeMap.Disabled._id,
      genericStatusesCodeMap.Enabled._id,
      handleSuccess,
      initialValues.billEmitterIds,
      navigate,
      personId,
      queryClient,
      updateEmittersMutation,
      updatePersonMutation,
    ],
  );

  return { handleSubmit };
};
